/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

export interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  /**
   * The class to add to the root element when the theme is dark
   * @default "dark-mode"
   */
  darkModeClass?: string;
  /**
   * The default theme to use if no theme is stored in localStorage
   * @default "system"
   */
  defaultTheme?: Theme;
  /**
   * The key to use to store the theme in localStorage
   * @default "ui-theme"
   */
  storageKey?: string;
}

export const ThemeProvider = ({
  children,
  storageKey = "ui-theme",
  darkModeClass = "dark-mode",
}: ThemeProviderProps) => {
  // Always use light theme - ignore saved preferences for now
  const [theme] = useState<Theme>("light");

  // Simplified setTheme that does nothing (for future re-enabling)
  const setTheme = (newTheme: Theme) => {
    // No-op for now - can be re-enabled later
    console.log(`Theme change requested to ${newTheme}, but light mode is enforced`);
  };

  useEffect(() => {
    // Always apply light theme
    const root = window.document.documentElement;
    root.classList.remove(darkModeClass);

    // Clear any saved theme preference
    localStorage.removeItem(storageKey);
  }, [darkModeClass, storageKey]);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
};
